/**
 * NeighborTier Component
 * 
 * Displays a tier (L1/L2/L3) of neighbors with expandable list.
 */

import React from 'react';
import { View, Text, Pressable, StyleSheet } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { color, space, text, icon, radius } from '../../../shared/tokens/mobile';
import { NeighborsByTier } from '../../../shared/types/parcel';
import { NeighborCard } from './NeighborCard';

interface NeighborTierProps {
  tier: NeighborsByTier;
  isExpanded: boolean;
  neighborSource: 'graph' | 'api_cache' | null;
  onToggle: () => void;
  onNeighborPress: (parcelId: string) => void;
}

export function NeighborTier({ tier, isExpanded, neighborSource, onToggle, onNeighborPress }: NeighborTierProps) {
  const tierLabel = tier.tier === 1 ? 'Direct Neighbors' : tier.tier === 2 ? 'Second Ring' : 'Third Ring';
  // Safely parse total_boundary_feet (may be string from API)
  const totalBoundaryFeet = (() => {
    const val = (tier as any).total_boundary_feet;
    if (val == null) return 0;
    const num = typeof val === 'string' ? parseFloat(val) : val;
    return isNaN(num) ? 0 : num;
  })();

  return (
    <View style={styles.container}>
      <Pressable style={styles.header} onPress={onToggle}>
        <View style={[styles.badge, tier.tier === 1 && styles.badgeL1]}>
          <Text style={[styles.badgeText, tier.tier === 1 && styles.badgeTextL1]}>L{tier.tier}</Text>
        </View>
        <View style={styles.info}>
          <Text style={styles.title}>{tierLabel}</Text>
          <Text style={styles.subtitle}>
            {tier.total_count} properties • {(() => {
              const acres = typeof tier.total_acres === 'number' ? tier.total_acres : parseFloat(String(tier.total_acres || 0)) || 0;
              return acres.toFixed(1);
            })()} acres • {tier.unique_owners || 0} owners
            {tier.tier === 1 && totalBoundaryFeet > 0 && (
              ` • ${Math.round(totalBoundaryFeet).toLocaleString()} ft shared`
            )}
          </Text>
        </View>
        <Ionicons 
          name={isExpanded ? 'chevron-up' : 'chevron-down'} 
          size={icon.sm} 
          color={color.textMuted} 
        />
      </Pressable>

      {isExpanded && (
        <View style={styles.list}>
          {tier.neighbors.length === 0 ? (
            <View style={styles.emptyTier}>
              <Text style={styles.emptyTierText}>No parcels in this tier</Text>
            </View>
          ) : (
            tier.neighbors.map((neighborData: any, idx: number) => {
              const neighbor = neighborData as any;
              return (
                <NeighborCard
                  key={neighbor.id || idx}
                  neighbor={neighbor}
                  tier={tier.tier}
                  isLast={idx === tier.neighbors.length - 1}
                  showInstantBadge={neighborSource === 'graph' && tier.tier === 1}
                  onPress={onNeighborPress}
                />
              );
            })
          )}
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: color.surface,
    borderRadius: radius.md,
    marginBottom: space.sm,
    borderWidth: 1,
    borderColor: color.divider,
    overflow: 'hidden',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: space.md,
  },
  badge: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: color.backgroundSecondary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badgeL1: {
    backgroundColor: color.primary,
  },
  badgeText: {
    fontSize: text.caption.fontSize,
    fontWeight: '700',
    color: color.textPrimary,
  },
  badgeTextL1: {
    color: color.surface,
  },
  info: {
    flex: 1,
    marginLeft: space.sm,
  },
  title: {
    fontSize: text.body.fontSize,
    fontWeight: text.bodyMedium.fontWeight,
    color: color.textPrimary,
  },
  subtitle: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
    marginTop: 2,
  },
  list: {
    borderTopWidth: 1,
    borderTopColor: color.divider,
  },
  emptyTier: {
    padding: space.md,
    alignItems: 'center',
  },
  emptyTierText: {
    fontSize: text.caption.fontSize,
    color: color.textMuted,
    fontStyle: 'italic',
  },
});

